import React, { useCallback } from 'react';
import { PanelId } from '../types';
import { useUI } from '../contexts';

type DropProps = {
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
};

type DragHandleProps = {
  draggable: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
};

type DividerProps = {
  className: string;
  onMouseDown?: (e: React.MouseEvent) => void;
} | null;

type UseLayoutPanelsParams = {
  startResize: (target: 'sidebar' | 'preview', anchor: 'left' | 'right', initialX: number) => void;
};

export function useLayoutPanels({
  startResize,
}: UseLayoutPanelsParams) {
  // Get UI state from context
  const {
    panelOrder,
    setPanelOrder,
    draggingPanel,
    setDraggingPanel,
    sidebarWidth,
    previewWidth,
  } = useUI();
  const handlePanelDrop = useCallback(
    (target: PanelId) => {
      if (!draggingPanel || draggingPanel === target) return;
      const next = panelOrder.filter((p) => p !== draggingPanel);
      const targetIndex = next.indexOf(target);
      if (targetIndex === -1) return;
      next.splice(targetIndex, 0, draggingPanel);
      setPanelOrder(next);
      setDraggingPanel(null);
    },
    [draggingPanel, panelOrder, setPanelOrder, setDraggingPanel]
  );

  const getDropProps = useCallback(
    (panel: PanelId): DropProps => {
      const dropAllowed = !!draggingPanel && draggingPanel !== panel;
      return {
        onDragOver: (e: React.DragEvent) => {
          if (dropAllowed) e.preventDefault();
        },
        onDrop: (e: React.DragEvent) => {
          if (!draggingPanel) return;
          e.preventDefault();
          handlePanelDrop(panel);
        },
      };
    },
    [draggingPanel, handlePanelDrop]
  );

  const getDragHandleProps = useCallback(
    (panel: PanelId): DragHandleProps => ({
      draggable: true,
      onDragStart: () => setDraggingPanel(panel),
      onDragEnd: () => setDraggingPanel(null),
    }),
    [setDraggingPanel]
  );

  const getPanelStyle = useCallback(
    (panel: PanelId): React.CSSProperties => {
      if (panel === 'sidebar') {
        return { flex: `0 0 ${sidebarWidth}px` };
      }
      if (panel === 'preview') {
        return { flex: `0 0 ${previewWidth}px` };
      }
      return { flex: 1, minWidth: 320 };
    },
    [sidebarWidth, previewWidth]
  );

  const getPanelClassName = useCallback(
    (panel: PanelId): string => {
      const isDragging = draggingPanel === panel;
      const dropAllowed = !!draggingPanel && draggingPanel !== panel;
      return [panel, isDragging ? 'dragging' : '', dropAllowed ? 'drop-target' : '']
        .filter(Boolean)
        .join(' ');
    },
    [draggingPanel]
  );

  const getDividerProps = useCallback(
    (index: number): DividerProps => {
      if (index >= panelOrder.length - 1) return null;
      const left = panelOrder[index];
      const right = panelOrder[index + 1];
      const adjustableLeft = left === 'sidebar' || left === 'preview';
      const adjustableRight = right === 'sidebar' || right === 'preview';
      if (!adjustableLeft && !adjustableRight) return { className: 'divider' };
      return {
        className: 'divider',
        onMouseDown: (e: React.MouseEvent) => {
          const target = (adjustableLeft ? left : right) as 'sidebar' | 'preview';
          const anchor = adjustableLeft ? 'right' : 'left';
          startResize(target, anchor, e.clientX);
        },
      };
    },
    [panelOrder, startResize]
  );

  return {
    handlePanelDrop,
    getDropProps,
    getDragHandleProps,
    getPanelStyle,
    getPanelClassName,
    getDividerProps,
  };
}
